import { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { AppDispatch, RootState } from "@app/store";
import { fetchTrackOrder } from "@app/store/sharedThunks";
import { clearOrder } from "@app/store/order.slice";
import { environment } from "@app/config/environment";
import { t } from "@app/utils/translation";
import { TrackOrderInput } from "./TrackOrderInput";
import { SearchIllustrationIcon } from "./icons/SearchIllustrationIcon";
import { TrackBarcodeIcon } from "./icons/TrackBarcodeIcon";
import styles from "./SearchBox.module.css";

const MIN_ORDER_CODE_LENGTH = 4;

export const SearchBox = () => {
  const orderType = useSelector((state: RootState) => state.order.type);
  const trackOrderCode = useSelector((state: RootState) => state.order.trackOrderCode);
  const fetchOrderError = useSelector((state: RootState) => state.order.error);
  const fetchOrderStatus = useSelector((state: RootState) => state.order.orderFetchStatus);
  const language = useSelector((state: RootState) => state.setting.language);

  const [orderCode, setOrderCode] = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const isTrackDisabled = orderCode.length < MIN_ORDER_CODE_LENGTH;

  const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderCode(e.target.value.trim());
  };

  const trackOrder = () => {
    if (isTrackDisabled) return;

    let restUrl = undefined;
    if (environment.isDevelopment) restUrl = environment.localRestUrl;
    dispatch(clearOrder());
    dispatch(fetchTrackOrder({ code: orderCode, language: null, restUrl }));
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") trackOrder();
  };

  useEffect(() => {
    navigate(`/${trackOrderCode}`, { replace: true });
  }, [orderType]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.column}>
        <SearchIllustrationIcon />

        <span className={styles.headline}>{t("TrackYourOrder", language)}</span>
        <span className={styles.subtitle}>{t("CheckTheStatus", language)}</span>

        <TrackOrderInput
          icon={<TrackBarcodeIcon />}
          placeholder={t("TypeTrackNumber", language)}
          helperText={(fetchOrderError && fetchOrderError.message) || t("TypeNoSpace", language)}
          onChange={inputOnChange}
          onKeyDown={onKeyDown}
          isError={!!fetchOrderError}
        />

        <button type="button" className={styles.trackButton} disabled={isTrackDisabled} onClick={trackOrder}>
          {fetchOrderStatus === "loading" ? <span className={styles.spinner} /> : <span>{t("Track", language)}</span>}
        </button>
      </div>
    </div>
  );
};

export const MemoSearchBox = memo(SearchBox);
